import type { ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router';
import { cx } from '@/lib/cx';
import { useAuth } from '@/lib/auth/AuthContext';
import { Avatar, Wordmark } from '@/components/brand/Brand';
import {
  BallOnTeeIcon,
  CartIcon,
  ChartIcon,
  TimelineIcon,
  TreeIcon,
} from '@/components/icons/NavIcons';
import { t, type PlainMessageKey } from '@/lib/i18n';
import { OfflineBanner, useOnline } from '@/components/ui/Feedback';
import { routes } from '@/routes/paths';
import styles from './AppShell.module.css';

interface Destination {
  to: string;
  label: PlainMessageKey;
  icon: (props: { size?: number }) => ReactNode;
  /** The gold FAB slot. Exactly one destination may claim it. */
  accent?: boolean;
  end?: boolean;
}

/**
 * A social entrou **sem custar um slot** (canvas 1p): a timeline tomou a primeira posição, que
 * era do resumo pessoal, e o resumo desceu para Stats. Continuam cinco destinos, e Campos segue
 * na quinta posição.
 */
const DESTINATIONS: Destination[] = [
  { to: routes.home, label: 'nav.timeline', icon: TimelineIcon, end: true },
  { to: routes.rounds, label: 'nav.rounds', icon: CartIcon },
  { to: routes.record, label: 'nav.record', icon: BallOnTeeIcon, accent: true },
  { to: routes.stats, label: 'nav.stats', icon: ChartIcon },
  { to: routes.courses, label: 'nav.courses', icon: TreeIcon },
];

/**
 * No desktop cabe o rótulo por extenso. "Stats" ficou de fora: a barra do canvas diz `Stats` nos
 * dois tamanhos, e escrever "Estatísticas" no desktop faria a aba discordar do título da tela que
 * ela abre.
 */
const DESKTOP_LABEL: Partial<Record<PlainMessageKey, PlainMessageKey>> = {
  'nav.record': 'nav.recordLong',
};

export function AppShell() {
  const { user } = useAuth();
  const online = useOnline();
  const name = user?.displayName ?? '';

  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href="#conteudo">
        {t('nav.skipToContent')}
      </a>

      {/* A connection problem is a state, not an event — so it persists as a strip
          rather than firing a toast that disappears before it is read. */}
      {!online && <OfflineBanner />}

      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <NavLink to={routes.home} aria-label={t('nav.aria.home')}>
            <Wordmark height={23} />
          </NavLink>
          <nav className={styles.headerNav} aria-label={t('nav.aria.main')}>
            {DESTINATIONS.filter((d) => !d.accent).map((d) => (
              <NavLink
                key={d.to}
                to={d.to}
                end={d.end}
                className={({ isActive }) => cx(styles.headerLink, isActive && styles.headerActive)}
              >
                {t(DESKTOP_LABEL[d.label] ?? d.label)}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className={styles.headerRight}>
          <NavLink to={routes.record} className={styles.accentPill}>
            {t('nav.recordLong')}
          </NavLink>
          <NavLink to={routes.profile} className={styles.avatarLink} aria-label={t('nav.aria.profile')}>
            <Avatar name={name} size={32} />
          </NavLink>
        </div>
      </header>

      <main className={styles.main} id="conteudo">
        <Outlet />
      </main>

      <nav className={styles.bottomNav} aria-label={t('nav.aria.main')}>
        {DESTINATIONS.map((d) => (
          <NavLink
            key={d.to}
            to={d.to}
            end={d.end}
            className={({ isActive }) =>
              cx(styles.navItem, isActive && !d.accent && styles.navActive)
            }
          >
            {d.accent ? (
              <>
                <span className={styles.fab}>
                  <d.icon size={18} />
                </span>
                <span className={styles.fabLabel}>{t(d.label)}</span>
              </>
            ) : (
              <>
                <d.icon size={20} />
                <span>{t(d.label)}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
